import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { StatusChip, type StatusTone } from '@/components/ui/Chip';
import { ResponsiveTable } from '@/components/ui/Table';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { CommunicationPreferenceCard } from '@/components/patient/consent/CommunicationPreferenceCard';
import { RegisterConsentSlideOver } from '@/components/patient/consent/RegisterConsentSlideOver';
import { RequestConsentSlideOver } from '@/components/patient/consent/RequestConsentSlideOver';
import { WithdrawConsentSlideOver } from '@/components/patient/consent/WithdrawConsentSlideOver';
import { TYPE } from '@/lib/typography';
import { uiDateLocale } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { cn } from '@/utils/cn';
import { usePatient } from '@/hooks/usePatients';
import {
  useConsentRecords,
  useConsentTextVersions,
  usePatientConsentCurrent,
  type ConsentRecordRow,
  type PatientConsentCurrentRow,
} from '@/hooks/useConsentRecords';
import {
  useCancelConsentRequest,
  usePatientConsentRequests,
  type ConsentRequestWithItems,
} from '@/hooks/useConsentRequests';
import {
  buildConsentStatusRows,
  consentStatusLabelKey,
  type ConsentDisplayStatus,
  type ConsentStatusRow,
} from '@/lib/consent/consentStatus';
import {
  displayConsentRequestStatus,
  isConsentRequestOpen,
  type ConsentRequestStatus,
  type ConsentTypeKey,
} from '@/lib/consent/consentTypes';

interface ConsentTabProps {
  clinicId: string;
  patientId: string;
}

function statusTone(status: ConsentDisplayStatus): StatusTone {
  switch (status) {
    case 'given':
      return 'completed';
    case 'refused':
      return 'coral';
    case 'withdrawn':
      return 'neutral';
    case 'expired':
      return 'draft';
    case 'not_set':
      return 'neutral';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function requestTone(status: ConsentRequestStatus): StatusTone {
  switch (status) {
    case 'pending':
      return 'upcoming';
    case 'answered':
      return 'completed';
    case 'expired':
      return 'draft';
    case 'cancelled':
      return 'cancelled';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function formatDate(value: string | null | undefined, withTime = false): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(uiDateLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

function scopeText(row: { scope_recipient_name: string | null; scope_purpose: string | null }): string {
  return [row.scope_recipient_name, row.scope_purpose].filter(Boolean).join(' · ');
}

export function ConsentTab({ clinicId, patientId }: ConsentTabProps) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const patient = usePatient(patientId, clinicId);
  const current = usePatientConsentCurrent(patientId, clinicId);
  const records = useConsentRecords(patientId, clinicId);
  const versions = useConsentTextVersions(clinicId);
  const requests = usePatientConsentRequests(patientId, clinicId);
  const cancelRequest = useCancelConsentRequest();

  const canCreate = hasPermission(clinicId, 'consent', 'create');
  const canEditCommunication = hasPermission(clinicId, 'patients', 'update');

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerType, setRegisterType] = useState<ConsentTypeKey | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [withdrawRow, setWithdrawRow] = useState<PatientConsentCurrentRow | null>(null);

  const textVersions = useMemo(() => versions.data ?? [], [versions.data]);
  const versionLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of textVersions) map.set(v.id, v.version_label);
    return map;
  }, [textVersions]);
  const statusRows = useMemo(() => buildConsentStatusRows(current.data ?? []), [current.data]);
  const activeCount = statusRows.filter((row) => row.current?.is_active).length;

  const openRegister = (type: ConsentTypeKey | null) => {
    setRegisterType(type);
    setRegisterOpen(true);
  };

  const handleCancelRequest = async (request: ConsentRequestWithItems) => {
    try {
      await cancelRequest.mutateAsync(request.id);
      toast.success(t('consent.requests.cancelled'));
    } catch (err) {
      console.error('cancel_consent_request failed', err);
      toast.error(t('consent.requests.cancel_error'));
    }
  };

  const loading = current.isLoading || records.isLoading;
  const error = current.error || records.error;

  return (
    <>
      <PatientTabFrame
        title={t('patients.tabs.consent')}
        count={activeCount}
        action={
          canCreate ? (
            <Button type="button" onClick={() => openRegister(null)}>
              {t('consent.register.open')}
            </Button>
          ) : undefined
        }
        loading={loading}
        error={error ? t('consent.tab.load_error') : undefined}
        onRetry={() => {
          void current.refetch();
          void records.refetch();
        }}
      >
        <div className="mt-s4 space-y-s6">
          <section aria-labelledby="consent-status-title">
            <h3 id="consent-status-title" className={cn(TYPE.section, 'mb-s3 text-ds-text')}>
              {t('consent.tab.status_title')}
            </h3>
            <ResponsiveTable<ConsentStatusRow>
              columns={[
                {
                  id: 'type',
                  header: t('consent.fields.type'),
                  cell: (row) => (
                    <div className="min-w-0">
                      <p className={cn(TYPE.body, 'text-ds-text')}>{t(`consent.types.${row.consentType}`)}</p>
                      {row.current && scopeText(row.current) ? (
                        <p className={cn(TYPE.small, 'text-ds-muted')}>{scopeText(row.current)}</p>
                      ) : null}
                    </div>
                  ),
                },
                {
                  id: 'status',
                  header: t('consent.fields.status'),
                  cell: (row) => (
                    <StatusChip tone={statusTone(row.status)}>
                      {t(consentStatusLabelKey(row.consentType, row.status))}
                    </StatusChip>
                  ),
                },
                {
                  id: 'version',
                  header: t('consent.fields.text_version'),
                  hideOnMobile: true,
                  cell: (row) =>
                    row.current?.text_version_id ? versionLabel.get(row.current.text_version_id) ?? '—' : '—',
                },
                {
                  id: 'decided',
                  header: t('consent.fields.decided_at'),
                  hideOnMobile: true,
                  cell: (row) => formatDate(row.current?.decided_at),
                },
                {
                  id: 'expires',
                  header: t('consent.fields.expires_at'),
                  hideOnMobile: true,
                  cell: (row) => formatDate(row.current?.expires_at),
                },
                {
                  id: 'actions',
                  header: <span className="sr-only">{t('consent.fields.actions')}</span>,
                  cell: (row) => renderActions(row),
                },
              ]}
              rows={statusRows}
              getRowKey={(row) => row.key}
              mobileCard={(row) => (
                <div className="space-y-s2">
                  <div className="flex items-center justify-between gap-s2">
                    <p className={TYPE.section}>{t(`consent.types.${row.consentType}`)}</p>
                    <StatusChip tone={statusTone(row.status)}>
                      {t(consentStatusLabelKey(row.consentType, row.status))}
                    </StatusChip>
                  </div>
                  {row.current ? (
                    <p className={cn(TYPE.small, 'text-ds-muted')}>
                      {[scopeText(row.current), formatDate(row.current.decided_at)].filter(Boolean).join(' · ')}
                      {row.current.expires_at
                        ? ` · ${t('consent.tab.expires', { date: formatDate(row.current.expires_at) })}`
                        : ''}
                    </p>
                  ) : null}
                  {renderActions(row)}
                </div>
              )}
            />
          </section>

          <section aria-labelledby="consent-requests-title">
            <div className="mb-s3 flex flex-wrap items-center gap-s3">
              <h3 id="consent-requests-title" className={cn(TYPE.section, 'flex-1 text-ds-text')}>
                {t('consent.requests.title')}
              </h3>
              {canCreate ? (
                <Button type="button" variant="secondary" onClick={() => setRequestOpen(true)}>
                  {t('consent.request.open')}
                </Button>
              ) : null}
            </div>
            {(requests.data ?? []).length === 0 ? (
              <p className={cn(TYPE.small, 'text-ds-muted')}>{t('consent.requests.empty')}</p>
            ) : (
              <ul className="divide-y divide-ds-line rounded-card border border-ds-line">
                {(requests.data ?? []).map((request) => {
                  const status = displayConsentRequestStatus(request);
                  return (
                    <li key={request.id} className="flex flex-wrap items-start gap-s3 p-s3">
                      <div className="min-w-0 flex-1 space-y-s1">
                        <div className="flex flex-wrap items-center gap-s2">
                          <StatusChip tone={requestTone(status)}>{t(`consent.request_status.${status}`)}</StatusChip>
                          <span className={cn(TYPE.small, 'text-ds-muted')}>
                            {t('consent.requests.sent_at', { date: formatDate(request.created_at, true) })}
                            {' · '}
                            {t('consent.requests.deadline', { date: formatDate(request.expires_at) })}
                          </span>
                        </div>
                        <ul className={cn(TYPE.small, 'text-ds-text')}>
                          {request.consent_request_items.map((item) => (
                            <li key={item.id}>
                              {t(`consent.types.${item.consent_type}`)}
                              {' – '}
                              <span className="text-ds-muted">
                                {item.response_record_id
                                  ? t('consent.requests.item_answered', { date: formatDate(item.responded_at) })
                                  : t('consent.requests.item_open')}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <p className={cn(TYPE.small, 'text-ds-muted')}>{notificationText(t, request)}</p>
                      </div>
                      {canCreate && isConsentRequestOpen(request) ? (
                        <Button
                          type="button"
                          variant="tertiary"
                          onClick={() => void handleCancelRequest(request)}
                          loading={cancelRequest.isPending && cancelRequest.variables === request.id}
                        >
                          {t('consent.requests.cancel')}
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section aria-labelledby="consent-history-title">
            <h3 id="consent-history-title" className={cn(TYPE.section, 'mb-s3 text-ds-text')}>
              {t('consent.tab.history_title')}
            </h3>
            {(records.data ?? []).length === 0 ? (
              <p className={cn(TYPE.small, 'text-ds-muted')}>{t('consent.tab.history_empty')}</p>
            ) : (
              <ol className="divide-y divide-ds-line rounded-card border border-ds-line">
                {(records.data ?? []).map((record) => (
                  <HistoryItem key={record.id} record={record} versionLabel={versionLabel} />
                ))}
              </ol>
            )}
          </section>

          <CommunicationPreferenceCard
            patientId={patientId}
            clinicId={clinicId}
            canEdit={canEditCommunication}
            legacySmsConsent={patient.data?.sms_consent}
          />
        </div>
      </PatientTabFrame>

      {canCreate ? (
        <>
          <RegisterConsentSlideOver
            open={registerOpen}
            onClose={() => setRegisterOpen(false)}
            clinicId={clinicId}
            patientId={patientId}
            textVersions={textVersions}
            initialType={registerType}
          />
          <RequestConsentSlideOver
            open={requestOpen}
            onClose={() => setRequestOpen(false)}
            clinicId={clinicId}
            patientId={patientId}
            hasPortalAccess={Boolean(patient.data?.portal_user_id)}
            textVersions={textVersions}
            requests={requests.data ?? []}
          />
          <WithdrawConsentSlideOver current={withdrawRow} onClose={() => setWithdrawRow(null)} />
        </>
      ) : null}
    </>
  );

  function renderActions(row: ConsentStatusRow) {
    if (!canCreate) return null;
    return (
      <div className="flex flex-wrap justify-end gap-s2">
        {row.current?.is_active ? (
          <Button type="button" variant="tertiary" onClick={() => setWithdrawRow(row.current)}>
            {row.consentType === 'refusal_disclosure' || row.consentType === 'refusal_lookup'
              ? t('consent.withdraw.open_refusal')
              : t('consent.withdraw.open')}
          </Button>
        ) : null}
        <Button type="button" variant="ghost" onClick={() => openRegister(row.consentType)}>
          {t('consent.register.open_short')}
        </Button>
      </div>
    );
  }
}

function notificationText(
  t: ReturnType<typeof useTranslation>['t'],
  request: ConsentRequestWithItems,
): string {
  if (!request.notification_status) return t('consent.notification.none_yet');
  if (request.notification_status === 'sent') {
    return request.notification_channel === 'sms'
      ? t('consent.notification.sent_sms', { date: formatDate(request.notified_at, true) })
      : t('consent.notification.sent_email', { date: formatDate(request.notified_at, true) });
  }
  const reason = request.notification_detail
    ? t(`consent.notification.reason.${request.notification_detail}`, {
        defaultValue: t('consent.notification.reason.unknown'),
      })
    : t('consent.notification.reason.unknown');
  return request.notification_status === 'failed'
    ? t('consent.notification.failed', { reason })
    : t('consent.notification.skipped', { reason });
}

function HistoryItem({ record, versionLabel }: { record: ConsentRecordRow; versionLabel: Map<string, string> }) {
  const { t } = useTranslation();
  const status: ConsentDisplayStatus = record.decision;
  const details = [
    t(`consent.method.${record.method}`),
    record.given_by_kind !== 'patient'
      ? `${t(`consent.given_by.${record.given_by_kind}`)}${record.given_by_name ? ` (${record.given_by_name})` : ''}`
      : null,
    record.text_version_id ? versionLabel.get(record.text_version_id) ?? null : null,
    record.expires_at ? t('consent.tab.expires', { date: formatDate(record.expires_at) }) : null,
  ].filter(Boolean);
  return (
    <li className="space-y-s1 p-s3">
      <div className="flex flex-wrap items-center gap-s2">
        <span className={cn(TYPE.body, 'text-ds-text')}>{t(`consent.types.${record.consent_type}`)}</span>
        <StatusChip tone={statusTone(status)}>{t(consentStatusLabelKey(record.consent_type, status))}</StatusChip>
        <span className={cn(TYPE.small, 'ml-auto text-ds-muted')}>{formatDate(record.created_at, true)}</span>
      </div>
      {scopeText(record) ? <p className={cn(TYPE.small, 'text-ds-muted')}>{scopeText(record)}</p> : null}
      <p className={cn(TYPE.small, 'text-ds-muted')}>{details.join(' · ')}</p>
      {record.note ? <p className={cn(TYPE.small, 'whitespace-pre-wrap text-ds-text')}>{record.note}</p> : null}
    </li>
  );
}
